/**
 * Create Scenario Dialog
 *
 * Full-screen overlay that delegates to:
 * - ScenarioPathChooser (choose between template, manual, or AI)
 * - TemplateChooser (pick a starting template)
 * - ManualScenarioBuilder (free, with timeline + code lookup)
 * - AiScenarioBuilder ($1.99, AI-powered chat)
 */

import { useState, useEffect } from 'react';
import { FullScreenScenarioOverlay } from './scenario-builder/FullScreenScenarioOverlay';
import { ScenarioPathChooser } from './scenario-builder/ScenarioPathChooser';
import { TemplateChooser } from './scenario-builder/TemplateChooser';
import { ManualScenarioBuilder } from './scenario-builder/ManualScenarioBuilder';
import { AiScenarioBuilder } from './scenario-builder/AiScenarioBuilder';
import type { ScenarioTemplate } from '@/lib/scenarioTemplates';

export interface ScenarioTemplateData {
  name: string;
  description: string;
  duration: string;
  tags: string[];
  procedures: {
    code: string;
    codeType: string;
    description: string;
    phase: string;
    quantity: number;
    estimatedCost: number;
    setting?: 'inpatient' | 'outpatient' | 'unspecified';
  }[];
}

interface CreateScenarioDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onScenarioCreated?: (scenarioId: string) => void;
  templateData?: ScenarioTemplateData | null;
  /** Pre-select family member ID when opening from FamilyPlanner */
  initialFamilyMemberId?: string | null;
}

export function CreateScenarioDialog({ open, onOpenChange, onScenarioCreated, templateData, initialFamilyMemberId }: CreateScenarioDialogProps) {
  const [path, setPath] = useState<'choose' | 'template' | 'manual' | 'ai'>('choose');
  const [selectedTemplate, setSelectedTemplate] = useState<ScenarioTemplateData | null>(null);
  const [currentStep, setCurrentStep] = useState(1);

  // When templateData is provided externally, skip straight to manual builder
  useEffect(() => {
    if (open && templateData) {
      setSelectedTemplate(templateData);
      setPath('manual');
      setCurrentStep(2);
    }
  }, [open, templateData]);

  const handleClose = () => {
    setPath('choose');
    setSelectedTemplate(null);
    setCurrentStep(1);
    onOpenChange(false);
  };

  const handleChoosePath = (chosen: 'manual' | 'ai' | 'template') => {
    if (chosen === 'template') {
      setPath('template');
    } else {
      setPath(chosen);
    }
  };

  const handleSelectTemplate = (template: ScenarioTemplate) => {
    setSelectedTemplate(template);
    setPath('manual');
    setCurrentStep(2);
  };

  const handleStartFromScratch = () => {
    setSelectedTemplate(null);
    setPath('manual');
    setCurrentStep(1);
  };

  // Determine step labels based on path
  const stepLabels = path === 'ai'
    ? ['Chat with Dignity', 'Review & Save']
    : ['Basic Info', 'Procedures & Timeline', 'Review & Share'];

  return (
    <FullScreenScenarioOverlay
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          setPath('choose');
          setSelectedTemplate(null);
          setCurrentStep(1);
        }
        onOpenChange(isOpen);
      }}
      step={path === 'choose' || path === 'template' ? 0 : currentStep}
      stepLabels={stepLabels}
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {path === 'choose' && (
          <div className="max-w-3xl mx-auto">
            <ScenarioPathChooser onChoose={handleChoosePath} onNavigateAway={handleClose} />
          </div>
        )}

        {path === 'template' && (
          <TemplateChooser
            onSelectTemplate={handleSelectTemplate}
            onStartFromScratch={handleStartFromScratch}
          />
        )}

        {path === 'manual' && (
          <ManualScenarioBuilder
            onBack={() => {
              setSelectedTemplate(null);
              setPath(selectedTemplate ? 'template' : 'choose');
              setCurrentStep(1);
            }}
            onCreated={onScenarioCreated}
            onClose={handleClose}
            onStepChange={setCurrentStep}
            templateData={selectedTemplate ?? templateData ?? undefined}
            initialFamilyMemberId={initialFamilyMemberId}
          />
        )}

        {path === 'ai' && (
          <AiScenarioBuilder
            onBack={() => setPath('choose')}
            onCreated={onScenarioCreated}
            onClose={handleClose}
            onStepChange={setCurrentStep}
          />
        )}
      </div>
    </FullScreenScenarioOverlay>
  );
}
